import React, { useEffect, useState } from 'react';
import { Paper, Button, Box, Grid2, Chip } from '@mui/material';
import { Timestamp, doc, updateDoc } from 'firebase/firestore';
import { Typography, Stack, Avatar, Select, MenuItem } from '@mui/material';
import palette from '../assets/sass/palette.scss';
import { useGameUtils } from '../utilities/GameUtils';
// eslint-disable-next-line no-unused-vars
import { useApi } from '../services/api';
// eslint-disable-next-line no-unused-vars
import { useAuth } from '../contexts/AuthContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import PlayerStatPill from './PlayerStatPill';

const GameFinalise = ({
  gameData,
  onFinalise,
  matchNumber,
  allSelectedPlayers = [],
  onSlotPlayersChange,
  loading,
  userHasAcceptedADate,
}) => {
  const { db } = useFirebase();
  // eslint-disable-next-line no-unused-vars
  const { post } = useApi();
  // eslint-disable-next-line no-unused-vars
  const { currentUser } = useAuth();
  const { createCombinedInvitationsPlayers, getAllUserDetailsByUserIds } = useGameUtils();
  const [allUsers, setAllUsers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState();
  // eslint-disable-next-line no-unused-vars
  const [amountOfMatches, setAmountOfMatches] = useState(2);
  const [amountOfPlayers, setAmountOfPlayers] = useState(0);
  const [teaTime, setTeaTime] = useState();
  const [plusModalOpen, setPlusModalOpen] = useState(false);
  const [plusModalSlot, setPlusModalSlot] = useState(null);
  const [slotPlayers, setSlotPlayers] = useState([null, null, null, null]);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeDialogSlot, setRemoveDialogSlot] = useState(null);
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [isAmending, setIsAmending] = useState(false);
  const totalValidSlotPlayers = slotPlayers.filter((i) => i !== null).length;

  const isMatchFinalized = gameData.matches?.[matchNumber - 1]?.status === 'finalised';
  const isSetMatchDisabled = () => {
    if (totalValidSlotPlayers < 2 && selectedFormat === 'match') {
      return true;
    }
    return isMatchFinalized && !isAmending;
  };

  useEffect(() => {
    const combinedInvitationsPlayers = createCombinedInvitationsPlayers(gameData);
    const getAllUsers = async () => {
      const users = await getAllUserDetailsByUserIds(
        combinedInvitationsPlayers.map((user) => user.user)
      );
      setAllUsers(users);

      // Initialize slotPlayers from gameData.matches if it exists
      if (gameData.matches && gameData.matches[matchNumber - 1]) {
        const match = gameData.matches[matchNumber - 1];
        if (match.players && match.players.length > 0) {
          const initialSlotPlayers = Array(4).fill(null);
          match.players.forEach((player, index) => {
            // Find the full user data from allUsers
            const fullUserData = users.find((u) => u.user === player.uid);
            if (fullUserData) {
              initialSlotPlayers[index] = {
                ...fullUserData,
                group: player.group,
                handicap: fullUserData.handicap || 0,
              };
            }
          });
          setSlotPlayers(initialSlotPlayers);

          // Set amountOfPlayers based on the number of players in the match
          if (match.format === 'match') {
            setAmountOfPlayers(match.players.length === 2 ? '1v1' : '2v2');
          } else {
            setAmountOfPlayers(match.players.length);
          }
        }
        if (match.teaTime) {
          setTeaTime(dayjs(match.teaTime.toDate()));
        } else {
          // Use gameData.date as default if no match tee time exists
          const defaultDate = gameData.date?.toDate ? gameData.date.toDate() : gameData.date;
          // Extract just the time from the game date
          const gameDate = dayjs(defaultDate);
          const timeOnly = dayjs().hour(gameDate.hour()).minute(gameDate.minute());
          setTeaTime(timeOnly);
        }
      } else {
        // No existing match, use gameData.date as default
        const defaultDate = gameData.date?.toDate ? gameData.date.toDate() : gameData.date;
        // Extract just the time from the game date
        const gameDate = dayjs(defaultDate);
        const timeOnly = dayjs().hour(gameDate.hour()).minute(gameDate.minute());
        setTeaTime(timeOnly);
      }
    };
    getAllUsers();

    // Set the format based on gameData or match data
    const matchFormat = gameData.matches?.[matchNumber - 1]?.format;
    const defaultFormat = matchFormat || gameData.format || 'match';
    setSelectedFormat(defaultFormat);

    // Only set default amountOfPlayers if there are no existing players
    const existingMatch = gameData.matches?.[matchNumber - 1];
    if (!existingMatch?.players?.length) {
      if (defaultFormat === 'match') {
        setAmountOfPlayers('1v1'); // Default to 1v1 for match play
      } else {
        setAmountOfPlayers(2); // Default to 2 for stroke play
      }
    }
  }, [gameData, matchNumber, createCombinedInvitationsPlayers, getAllUserDetailsByUserIds]);

  const handleAmountOfPlayersChange = (event) => {
    setAmountOfPlayers(event.target.value);
  };

  const handleFormatChange = (event) => {
    if (event.target.value !== null) {
      const newFormat = event.target.value;
      setSelectedFormat(newFormat);

      // Set default amount of players based on new format
      if (newFormat === 'match') {
        setAmountOfPlayers('1v1');
      } else {
        setAmountOfPlayers(2);
      }

      // Update the game data through the parent
      if (onFinalise) {
        onFinalise({
          slotPlayers: slotPlayers.filter(Boolean),
          matchNumber,
          selectedFormat: newFormat,
          matchGroupingType: amountOfPlayers === '1v1' ? '1v1' : '2v2',
          teaTime: Timestamp.fromDate(teaTime.toDate()),
          status: isMatchFinalized ? 'finalised' : 'pending',
        });
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAmountOfMatchesChange = (event) => {
    if (event.target.value !== null) {
      setAmountOfMatches(event.target.value);
    }
  };


  const handleLockInPlayers = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isMatchFinalized && !isAmending) {
      setAmendDialogOpen(true);
      return;
    }

    if (onFinalise) {
      try {
        // Determine how many slots are active based on format and amount
        const activeSlotCount =
          selectedFormat === 'match'
            ? amountOfPlayers === '1v1'
              ? 2
              : 4
            : Number(amountOfPlayers);

        console.log('Debug - amountOfPlayers:', amountOfPlayers);
        console.log('Debug - activeSlotCount:', activeSlotCount);
        console.log('Debug - slotPlayers before:', slotPlayers);

        // Only take the active slots and filter out nulls
        const validSlotPlayers = slotPlayers
          .slice(0, activeSlotCount)
          .filter(Boolean)
          .map((player, index) => {
            // Force group assignment based on index and match type
            const group =
              amountOfPlayers === '1v1' ? (index === 0 ? 'A' : 'B') : index < 2 ? 'A' : 'B';
            console.log(`Debug - Player ${index}:`, {
              player,
              index,
              amountOfPlayers,
              assignedGroup: group,
              existingGroup: player.group,
            });
            return {
              ...player,
              group,
              handicap: player.handicap || 0,
              waglRating: player.waglRating || 50,
              uid: player.user || player.uid,
              user: player.user || player.uid,
            };
          });

        console.log('Debug - validSlotPlayers after:', validSlotPlayers);

        await onFinalise({
          slotPlayers: validSlotPlayers,
          matchNumber,
          selectedFormat,
          matchGroupingType: amountOfPlayers === '1v1' ? '1v1' : '2v2',
          teaTime: Timestamp.fromDate(teaTime.toDate()),
          status: 'finalised',
        });

        // Reset amending state after successful lock in
        setIsAmending(false);
      } catch (error) {
        console.error('Error locking in players:', error);
      }
    }
  };

  const handleAmendConfirm = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setAmendDialogOpen(false);
    setIsAmending(true);

    // Update local state to reflect pending status
    const updatedGameData = { ...gameData };
    if (updatedGameData.matches && updatedGameData.matches[matchNumber - 1]) {
      updatedGameData.matches[matchNumber - 1].status = 'pending';
    }

    // Update database with pending status
    const gameRef = doc(db, 'games', gameData.id);
    await updateDoc(gameRef, {
      matches: updatedGameData.matches,
    });

    // Send updated status to parent
    if (onFinalise) {
      console.log('Sending updated status to parent');
      onFinalise({
        slotPlayers: slotPlayers.filter(Boolean),
        matchNumber,
        selectedFormat,
        matchGroupingType: amountOfPlayers === '1v1' ? '1v1' : '2v2',
        teaTime: Timestamp.fromDate(teaTime.toDate()),
        status: 'pending',
      });
    }
  };

  const handleAmendCancel = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setAmendDialogOpen(false);
    setIsAmending(false);
  };

  // const handlePlayerGroupSelect = (id, group) => {
  //   // person.group = group;
  //   // allUsers[index].group = group;
  //   const p = selectedPlayers.find((player) => player.user === id);
  //   p.group = group;
  //   setSelectedPlayers([...selectedPlayers]);
  // };


  const handlePlusClick = (slotIdx) => {
    setPlusModalSlot(slotIdx);
    setPlusModalOpen(true);
  };

  const handleModalClose = () => {
    setPlusModalOpen(false);
    setPlusModalSlot(null);
  };

  const handleModalPlayerSelect = (person) => {
    const newSlots = [...slotPlayers];
    // Determine group based on slot index and match type
    let group;
    if (amountOfPlayers === '1v1') {
      // For 1v1, first player is group A, second player is group B
      group = plusModalSlot === 0 ? 'A' : 'B';
    } else {
      // For 2v2, first two slots are group A, last two are group B
      group = plusModalSlot < 2 ? 'A' : 'B';
    }
    newSlots[plusModalSlot] = { ...person, group };
    setSlotPlayers(newSlots);
    setPlusModalOpen(false);
    setPlusModalSlot(null);
  };

  const handlePlayerSlotClick = (idx) => {
    if (isMatchFinalized && !isAmending) {
      // setAmendDialogOpen(true);
      return;
    }
    setRemoveDialogSlot(idx);
    setRemoveDialogOpen(true);
  };

  const handleRemoveDialogClose = () => {
    setRemoveDialogOpen(false);
    setRemoveDialogSlot(null);
  };

  const handleRemovePlayer = () => {
    if (removeDialogSlot !== null) {
      const newSlots = [...slotPlayers];
      newSlots[removeDialogSlot] = null;
      setSlotPlayers(newSlots);
    }
    setRemoveDialogOpen(false);
    setRemoveDialogSlot(null);
  };

  // Call parent callback when slotPlayers changes
  useEffect(() => {
    if (onSlotPlayersChange) {
      onSlotPlayersChange(slotPlayers, matchNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotPlayers]);

  // Helper to get slot count based on format and selection
  const getSlotCount = () => {
    if (selectedFormat === 'match') {
      if (amountOfPlayers === '1v1') return 2;
      if (amountOfPlayers === '2v2') return 4;
      return 2;
    } else {
      return Number(amountOfPlayers) || 2;
    }
  };
  const slotCount = getSlotCount();

  const VsDot = () => {
    return (
      <Grid2 size={{ xs: 12, md: 2 }} sx={{ textAlign: 'center', flexGrow: 1 }}>
        <Box
          sx={{
            backgroundColor: palette.darkGreen,
            color: 'white',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '48px auto',
          }}
        >
          <Typography variant="strong" sx={{ fontWeight: 700 }}>
            VS
          </Typography>
        </Box>
      </Grid2>
    );
  };

  const AddPlayerButton = ({ idx }) => {
    return (
      <Button
        onClick={() => handlePlusClick(idx)}
        sx={{
          borderRadius: '50%',
          minWidth: 0,
          width: 64,
          height: 64,
          border: `2px solid ${palette.mediumPurple}`,
          color: '#B299FF',
          p: 0,
          mx: 'auto',
        }}
      >
        <AddIcon sx={{ fontSize: 48 }} />
      </Button>
    );
  };

  const SelectedPlayer = ({ idx, player, children = null }) => {
    return (
      <Box
        onClick={() => handlePlayerSlotClick(idx)}
        sx={{ cursor: 'pointer', textAlign: 'center' }}
      >
        <Box
          sx={{
            borderRadius: '50%',
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 1,
          }}
        >
          {player.photoURL ? (
            <Avatar
              alt={player.userName || player.userId}
              src={player.photoURL}
              sx={{ width: 80, height: 80, boxShadow: 2 }}
            />
          ) : (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: idx % 2 ? palette.paleGreen : palette.palePurple,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="factorA"
                sx={{ fontSize: '24px', color: '#4C4C4C', fontWeight: 700 }}
              >
                {player.initial}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          sx={{
            fontFamily: '"FactorA Medium", arial, sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            textAlign: 'center',
            mb: 0.5,
          }}
        >
          {player.userName}
        </Typography>
        <PlayerStatPill
          waglScore={player?.waglRating?.toFixed(0) || 50}
          handicap={player.handicap || 0}
        />
        {children ?? children}
      </Box>
    );
  };

  const handleInteractiveElementClick = (e) => {
    if (isMatchFinalized && !isAmending) {
      e.preventDefault();
      e.stopPropagation();
      console.log('Opening amend dialog');
      setAmendDialogOpen(true);
    }
  };

  return (
    <Paper
      elevation={0}
      variant="gameBox"
      sx={{
        border: '2px solid black',
        position: 'relative',
        cursor: isMatchFinalized && !isAmending ? 'pointer' : 'default',
      }}
      onClick={handleInteractiveElementClick}
    >
      {isMatchFinalized && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: isAmending ? palette.mediumPurple : palette.darkGreen,
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          {isAmending ? <LockOpenIcon /> : <LockIcon />}
        </Box>
      )}
      <Typography variant="moduleHeader" component="h5">
        Match #{matchNumber}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ alignItems: 'center' }} spacing={2}>
        <Typography variant="fieldLabel">Format:</Typography>
        <Select
          sx={{ minWidth: '100px', borderColor: 'black' }}
          value={selectedFormat || ''}
          name="kind"
          onChange={handleFormatChange}
          displayEmpty
          onClick={handleInteractiveElementClick}
          disabled={isMatchFinalized && !isAmending}
        >
          <MenuItem value="" disabled>
            Select
          </MenuItem>
          <MenuItem value="stroke">Stroke Play</MenuItem>
          <MenuItem value="match">Match Play</MenuItem>
        </Select>
        <Typography variant="fieldLabel">Players:</Typography>
        <Select
          sx={{ minWidth: '100px' }}
          value={amountOfPlayers || ''}
          name="kind"
          onChange={handleAmountOfPlayersChange}
          displayEmpty
          onClick={handleInteractiveElementClick}
          disabled={isMatchFinalized && !isAmending}
        >
          {selectedFormat === 'match'
            ? [
                <MenuItem value="1v1" key="1v1">
                  1v1
                </MenuItem>,
                <MenuItem value="2v2" key="2v2">
                  2v2
                </MenuItem>,
              ]
            : [2, 3, 4].map((num) => (
                <MenuItem value={num} key={num}>
                  {num}
                </MenuItem>
              ))}
        </Select>
        <Typography variant="fieldLabel">Tee Time:</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={teaTime}
            onChange={(newValue) => setTeaTime(newValue)}
            views={['hours', 'minutes']}
            minTime={dayjs().isSame(dayjs(), 'day') ? dayjs() : null}
            onClick={handleInteractiveElementClick}
            disabled={isMatchFinalized && !isAmending}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-error': {
                  '& fieldset': {
                    borderColor: palette.borderPink,
                  },
                  '&:hover fieldset': {
                    borderColor: palette.borderPink,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: palette.borderPink,
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      </Stack>
      <Grid2
        container
        spacing={0}
        sx={{ mt: 2, mb: 2 }}
        columns={{ xs: 12, md: selectedFormat === 'match' ? 10 : 12 }}
      >
        {selectedFormat === 'match'
          ? Array.from({ length: slotCount }).map((_, idx) => {
              const player = slotPlayers[idx];
              return (
                <React.Fragment key={idx}>
                  <Grid2
                    sx={{ flexGrow: 1, pt: 2 }}
                    size={{ xs: 6, md: slotCount === 2 ? 4 : slotCount > 2 ? 2 : 6 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        alignItems: 'center',
                        width: '100%',
                        borderRight:
                          (idx < 1 || idx > 1) && idx + 1 < slotCount && slotCount > 2
                            ? `1px solid ${palette.borderGrey}`
                            : 'none',
                      }}
                    >
                      {player ? (
                        <SelectedPlayer idx={idx} player={player}>
                          {/* Group badge for 2v2 only */}
                          {slotCount === 4 && (
                            <Box sx={{ mt: 1 }}>
                              {/* <Chip
                                label={`Group ${group}`}
                                size="small"
                                color={group === 'A' ? 'primary' : 'success'}
                              /> */}
                            </Box>
                          )}
                        </SelectedPlayer>
                      ) : (
                        <AddPlayerButton idx={idx} />
                      )}
                    </Box>
                  </Grid2>
                  {/* Insert VS in the middle for match play */}
                  {slotCount === 2 && idx === 0 && <VsDot slotCount={slotCount} />}
                  {slotCount === 4 && idx === 1 && <VsDot slotCount={slotCount} />}
                </React.Fragment>
              );
            })
          : Array.from({ length: slotCount }).map((_, idx) => {
              // For stroke play (2-4 slots)
              const player = slotPlayers[idx];
              return (
                <React.Fragment key={idx}>
                  <Grid2 size={{ xs: 6, md: slotCount === 4 ? 3 : slotCount === 3 ? 4 : 6 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        alignItems: 'center',
                        width: '100%',
                        my: 2,
                        borderRight: {
                          xs: idx < 1 || idx === 2 ? `1px solid ${palette.borderGrey}` : 'none',
                          md: idx + 1 < slotCount ? `1px solid ${palette.borderGrey}` : 'none',
                        },
                      }}
                    >
                      {player ? (
                        <SelectedPlayer idx={idx} player={player} />
                      ) : (
                        <AddPlayerButton idx={idx} />
                      )}
                    </Box>
                  </Grid2>
                </React.Fragment>
              );
            })}
      </Grid2>
      <Dialog open={plusModalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Add Players
          <IconButton
            aria-label="close"
            onClick={handleModalClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid2 container spacing={2} sx={{ mt: 1 }}>
            {allUsers.map((person, index) => {
              // Check if selected in this match
              const alreadySelectedInThisMatch = slotPlayers.some(
                (p) => p && p.user === person.user
              );
              // Check if selected in another match
              const selectedInOtherMatch = allSelectedPlayers.find(
                (p) => p.user === person.user && p.matchNumber !== matchNumber
              );

              const isDisabled = alreadySelectedInThisMatch || !!selectedInOtherMatch || loading;
              return (
                <Grid2 size={{ x: 12, sm: 3 }} key={index}>
                  <Stack
                    direction="column"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.5 : 1,
                      pointerEvents: isDisabled ? 'none' : 'auto',
                      position: 'relative',
                      mb: 1,
                    }}
                    spacing={1}
                    onClick={() => {
                      if (!isDisabled) handleModalPlayerSelect(person);
                    }}
                  >
                    {person.photoURL ? (
                      <Avatar
                        alt={person.userName || person.userId}
                        sx={{
                          bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF',
                          width: '80px',
                          height: '80px',
                        }}
                        src={person.photoURL}
                      />
                    ) : (
                      <Box
                        sx={{
                          bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF',
                          width: '80px',
                          height: '80px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: '50%',
                        }}
                      >
                        <Typography
                          variant="factorA"
                          color="primary"
                          component="small"
                          sx={{ color: '#4C4C4C', fontSize: '24px', fontWeight: 700 }}
                        >
                          {person.initial}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="body1"
                        noWrap
                        sx={{
                          fontWeight: 700,
                          maxWidth: '100px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                          textAlign: 'center',
                        }}
                      >
                        <b>{person.userName}</b>
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}
                        sx={{ mt: 1 }}
                      >
                        {isDisabled ? (
                          <Typography variant="caption" sx={{ color: 'orange', fontWeight: 700 }}>
                            {alreadySelectedInThisMatch
                              ? 'In this match'
                              : selectedInOtherMatch
                              ? `In match #${selectedInOtherMatch.matchNumber}`
                              : ''}
                          </Typography>
                        ) : (
                          <PlayerStatPill
                            waglScore={person.waglRating?.toFixed(0) || 50}
                            handicap={person.handicap ? person.handicap : 0}
                          />
                        )}
                      </Stack>
                    </Box>
                    {userHasAcceptedADate(person) && (
                      <Chip className="chip_status" variant="statusAvailable" label="Accepted" />
                    )}
                    {!userHasAcceptedADate(person) && (
                      <Chip className="chip_status" variant="statusInvited" label="Not Accepted" />
                    )}
                  </Stack>
                </Grid2>
              );
            })}
          </Grid2>
        </DialogContent>
      </Dialog>
      <Dialog open={removeDialogOpen} onClose={handleRemoveDialogClose}>
        <DialogTitle>Remove Player</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Are you sure you want to remove this player?</Typography>
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 1 }}>
            <Button onClick={handleRemoveDialogClose} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleRemovePlayer} color="error" variant="contained">
              Remove
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      <Button
        variant="containedWagl"
        sx={{ mt: 3, width: '100%' }}
        onClick={handleLockInPlayers}
        disabled={isSetMatchDisabled()}
      >
        {isAmending ? 'Lock in match' : 'Set Match'}
      </Button>
      {/* Add Amend Dialog */}
      <Dialog
        open={amendDialogOpen}
        onClose={handleAmendCancel}
        onClick={(e) => {
          console.log('Dialog clicked');
          e.stopPropagation();
        }}
      >
        <DialogTitle>Match Already Locked In</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This match has already been locked in. Would you like to amend this match?
          </Typography>
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 1 }}>
            <Button
              onClick={(e) => {
                console.log('Cancel button clicked');
                handleAmendCancel(e);
              }}
              color="inherit"
            >
              No
            </Button>
            <Button
              onClick={(e) => {
                console.log('Confirm button clicked');
                handleAmendConfirm(e);
              }}
              color="primary"
              variant="contained"
            >
              Yes
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default GameFinalise;
